import { ColumnTable } from 'arquero'
import {
  CellPlotData,
  ScatterPlotData,
  ScatterPlotMetadata,
} from 'redux/slices/types'
import { CellInfoWithFilterFlag } from '../tsv/types'
import { Combination } from './generateCellInfoCombinations'
import { addNewPlotToData, addToExistingPlot } from './utils'
import { BasePlotParams } from './types'
import { getColumn } from '../arrow/arqueroHelpers'

/**
 * Helper to generate combinations from column data (memory-efficient)
 */
function generateCombinationsFromColumns(
  rowIndex: number,
  comparisonDimensionColumns: Map<string, (string | number)[]>,
  dimensions: Array<{ field: string; values: string[] }>
): Combination[] {
  const combinations: Combination[] = [{ key: '', exists: true }]

  dimensions.forEach(({ field, values }, fieldIndex) => {
    const newCombinations: Combination[] = []
    const lastField = fieldIndex === dimensions.length - 1
    const column = comparisonDimensionColumns.get(field)
    const cellValue = column ? column[rowIndex] : null

    values.forEach((value) => {
      combinations.forEach(({ key, exists }) => {
        // Convert both to strings for comparison to handle bigint, number, and string types
        const cellValueStr = String(cellValue ?? '')
        const expectedValueStr = String(value)

        const newCombination = {
          key: `${key}${field}:${value}${lastField ? '' : '&'}`,
          exists: exists && cellValueStr === expectedValueStr,
        }

        newCombinations.push(newCombination)
      })
    })

    combinations.splice(0, combinations.length, ...newCombinations)
  })

  return combinations
}

type GetScatterPlotDataFromDataFrameParams = {
  scatterPlotMetaData: ScatterPlotMetadata
  dataFrame: ColumnTable
  prefilteredCellIdSet: Set<string>
  hiddenCellInfoGroupValues: Record<string, string[]>
  selectedCellInfoGroupName: string
  cellInfoGroups: Record<string, { data: Array<{ value: string; color: string }>; isContinuous: boolean }>
  categoricalColorPalette: string[]
  getBasePlot: (params: BasePlotParams) => CellPlotData
  visibleSelectedComparisonDimensions?: Array<{ field: string; values: string[] }>
  numOfColumns?: number
}

/**
 * DataFrame version of getScatterPlotDataFromCellInfo
 * Works directly with columnar data without converting to objects
 * Memory-efficient for large datasets (100k+ cells)
 */
export const getScatterPlotDataFromDataFrame = (
  params: GetScatterPlotDataFromDataFrameParams
): Omit<ScatterPlotData, 'scatterPlotMetaData' | 'scatterPlotMetaDataFiltered'> => {
  const {
    scatterPlotMetaData,
    dataFrame,
    prefilteredCellIdSet,
    hiddenCellInfoGroupValues,
    selectedCellInfoGroupName,
    cellInfoGroups,
    categoricalColorPalette,
    getBasePlot,
    visibleSelectedComparisonDimensions,
    numOfColumns = 5,
  } = params

  const plotData: CellPlotData[] = []
  // NOTE: DataFrame version does NOT build cellInfo array to save memory
  // Cell data can be accessed on-demand via getCellInfoByCellId hook
  const outputCellInfo: CellInfoWithFilterFlag[] = []

  const { minX, minY, maxX, maxY } = scatterPlotMetaData
  const xOffset = (maxX - minX) * 1.5
  const yOffset = (maxY - minY) * 1.5

  // Extract columns from DataFrame (cached to prevent redundant .toArray() calls)
  const cellIdColumn = getColumn<string>(dataFrame, 'CELL_ID')
  const umapXColumn = getColumn<number>(dataFrame, 'UMAP_0')
  const umapYColumn = getColumn<number>(dataFrame, 'UMAP_1')

  if (cellIdColumn.length === 0 || umapXColumn.length === 0 || umapYColumn.length === 0) {
    return { plotData: [], cellInfo: [] }
  }

  // Extract selected dimension column if it exists (cached)
  const selectedColumn = selectedCellInfoGroupName
    ? getColumn<string | number>(dataFrame, selectedCellInfoGroupName)
    : null

  // Get hidden attribute columns (cached)
  const hiddenAttrNames = Object.keys(hiddenCellInfoGroupValues)
  const hiddenAttrColumns = new Map<string, (string | number)[]>()
  hiddenAttrNames.forEach((attrName) => {
    const column = getColumn<string | number>(dataFrame, attrName)
    if (column.length > 0) {
      hiddenAttrColumns.set(attrName, column)
    }
  })

  // Get all comparison dimension columns if needed (cached)
  const comparisonDimensionColumns = new Map<string, (string | number)[]>()
  if (visibleSelectedComparisonDimensions) {
    visibleSelectedComparisonDimensions.forEach((dim) => {
      const column = getColumn<string | number>(dataFrame, dim.field)
      if (column.length > 0) {
        comparisonDimensionColumns.set(dim.field, column)
      }
    })
  }

  const { isContinuous } = cellInfoGroups[selectedCellInfoGroupName] ?? {}
  const cellDataCount = cellIdColumn.length

  // Process each cell using columnar data
  for (let i = 0; i < cellDataCount; i += 1) {
    const cellId = cellIdColumn[i]
    const x = +umapXColumn[i]
    const y = +umapYColumn[i]

    // Check if removed (prefiltered)
    const isRemoved = prefilteredCellIdSet.has(cellId)

    // Check if hidden (has value in hidden attributes)
    let isHidden = isRemoved
    if (!isHidden) {
      for (let attrIndex = 0; attrIndex < hiddenAttrNames.length; attrIndex += 1) {
        const attrName = hiddenAttrNames[attrIndex]
        const attrColumn = hiddenAttrColumns.get(attrName)
        if (attrColumn) {
          const cellValue = attrColumn[i]
          const hiddenValues = hiddenCellInfoGroupValues[attrName]
          if (hiddenValues?.includes(String(cellValue))) {
            isHidden = true
            break
          }
        }
      }
    }

    // DataFrame version: Skip building cell objects entirely to save memory (2.6 GB → ~250 MB)
    // Cell info can be accessed on-demand via getCellInfoByCellId(cellId) hook
    // Only process visible cells for plot rendering
    if (!isHidden) {
      // Process visible cells for plot rendering (but don't build full cell objects)

      // Get name and color for visible cells
      const selectedCellValue = selectedColumn ? selectedColumn[i] : ''
      // Convert to string for comparison since cellInfoGroups stores all values as strings
      // (via String(value) in getCellInfoToGroupsFromDataFrame), but Arrow columns may
      // return numbers or bigints for older sessions (e.g., leiden stored as integer column)
      const selectedCellValueStr = String(selectedCellValue ?? '')
      const name = isContinuous ? '' : `${selectedCellInfoGroupName}${selectedCellValueStr}`

      const { color: defaultColor } =
        cellInfoGroups[selectedCellInfoGroupName]?.data.find(
          (c) => c.value === selectedCellValueStr
        ) ?? {}

      const color = isContinuous
        ? selectedCellValueStr
        : defaultColor ?? categoricalColorPalette[0]

      const cellIsDefined = name !== undefined && color !== undefined

      if (cellIsDefined) {
        // Handle comparison dimensions (multi-plot mode)
        if (visibleSelectedComparisonDimensions && visibleSelectedComparisonDimensions.length > 0) {
          // Generate combinations for this cell
          const splitOutput = generateCombinationsFromColumns(
            i,
            comparisonDimensionColumns,
            visibleSelectedComparisonDimensions
          )

          splitOutput.forEach((so, soIndex) => {
            const col = soIndex % numOfColumns
            const row = Math.floor(soIndex / numOfColumns)
            const { key, exists } = so

            if (exists) {
              const existingPlot = plotData.find((d) => d.name === key)

              if (existingPlot) {
                addToExistingPlot(
                  existingPlot,
                  x + xOffset * col,
                  y - yOffset * row,
                  x,
                  y,
                  cellId,
                  color
                )
              } else {
                addNewPlotToData(plotData, getBasePlot, {
                  x: x + xOffset * col,
                  y: y - yOffset * row,
                  originalX: x,
                  originalY: y,
                  name: key,
                  cellId,
                  color,
                })
              }
            }
          })
        } else {
          // Single plot mode
          const existingPlot = plotData.find((d) => d.name === name)

          if (existingPlot) {
            addToExistingPlot(existingPlot, x, y, x, y, cellId, color)
          } else {
            addNewPlotToData(plotData, getBasePlot, {
              x,
              y,
              originalX: x,
              originalY: y,
              name,
              cellId,
              color,
            })
          }
        }
      }
    }
  }

  return {
    plotData,
    cellInfo: outputCellInfo,
  }
}


export default getScatterPlotDataFromDataFrame
